/**
 * Batch-instance helpers for run graphs.
 *
 * When a run is batched (`totalBatchSize > 1`), the backend clones every vertex
 * and edge *inside the same graph*, reusing the same `vertexId` / `edgeId` and
 * copying `position` verbatim (see RunHelper.createBatch in ila-api). The only
 * field that separates the instances is `cloneNumber`.
 *
 * A snapshot for an N-batch run therefore contains N nodes sharing one `id` at
 * one position. maxGraph silently auto-renames colliding ids rather than
 * rejecting them, so hydrating such a snapshot directly stacks every instance
 * on top of the others and cross-wires their edges onto instance 1's cells.
 *
 * The fix is to render one instance at a time: derive the available instances
 * with `deriveBatches`, then narrow the snapshot with `filterSnapshotByClone`
 * before hydrating.
 */

import type { GraphSnapshot, MaterialNodeSpec } from './types';

/** A selectable batch instance, labelled by its batched material. */
export interface BatchDescriptor {
  cloneNumber: number;
  label: string;
}

/**
 * Batch instances are 1-based. Graphs authored before batching existed (and
 * every unbatched run) carry no `cloneNumber` at all — treat those as
 * instance 1 so they flow through the filter untouched.
 */
const DEFAULT_CLONE = 1;

function cloneOf(entity: { cloneNumber?: number }): number {
  return entity.cloneNumber ?? DEFAULT_CLONE;
}

/**
 * The batch instances present in a snapshot, ordered by instance number.
 *
 * Labels prefer the run's `batchedNodeId` when available: that lets us name each
 * clone after the matching batched material inside that clone, while the snapshot
 * itself still determines which clone numbers exist.
 *
 * Returns `[]` when the graph is not batched, which callers use to hide the
 * batch selector entirely.
 */
export function deriveBatches(
  snapshot: GraphSnapshot | null | undefined,
  batchedNodeId?: string | null,
): BatchDescriptor[] {
  if (!snapshot) return [];
  const byClone = new Map<number, MaterialNodeSpec[]>();
  for (const material of snapshot.materialNodes) {
    const cloneNumber = cloneOf(material);
    const materials = byClone.get(cloneNumber) ?? [];
    materials.push(material);
    byClone.set(cloneNumber, materials);
  }
  if (byClone.size <= 1) return [];

  return [...byClone.entries()]
    .map(([cloneNumber, materials]) => {
      const preferredMaterial = batchedNodeId
        ? materials.find((material) => {
            const ids = [material.id, material.vertexId].filter(Boolean).map(String);
            return ids.includes(String(batchedNodeId));
          })
        : undefined;
      const fallbackBatchMaterial = materials.find((material) => material.batchNode);
      const labelSource = preferredMaterial ?? fallbackBatchMaterial ?? materials[0];

      return {
        cloneNumber,
        label: labelSource?.name || `Batch ${cloneNumber}`,
      };
    })
    .sort((a, b) => a.cloneNumber - b.cloneNumber);
}

/**
 * The batch instance to actually render, given the user's explicit choice.
 *
 * Resolve this during render rather than syncing state in an effect: an effect
 * would let one paint through with the unfiltered snapshot, hydrating every
 * stacked instance before immediately remounting to correct it.
 *
 * Also self-corrects a stale selection — if a different graph loads that has no
 * such instance, this falls back to the first one instead of rendering empty.
 */
export function resolveActiveClone(
  snapshot: GraphSnapshot | null | undefined,
  selected: number | null,
): number | null {
  const batches = deriveBatches(snapshot);
  if (batches.length === 0) return null;
  if (selected != null && batches.some((b) => b.cloneNumber === selected)) return selected;
  return batches[0].cloneNumber;
}

/**
 * A snapshot narrowed to a single batch instance.
 *
 * Identity-returns the input when the graph is not batched, so unbatched runs
 * pay nothing and render exactly as before.
 */
export function filterSnapshotByClone(
  snapshot: GraphSnapshot,
  cloneNumber: number,
): GraphSnapshot {
  const matches = (entity: { cloneNumber?: number }) => cloneOf(entity) === cloneNumber;

  const materialNodes = snapshot.materialNodes.filter(matches);
  const operationNodes = snapshot.operationNodes.filter(matches);
  const flowEdges = snapshot.flowEdges.filter(matches);
  const groups = snapshot.groups.filter(matches);

  // Nothing was dropped — hand back the original object so React memoisation
  // and the provider `key` see a stable reference.
  if (
    materialNodes.length === snapshot.materialNodes.length &&
    operationNodes.length === snapshot.operationNodes.length &&
    flowEdges.length === snapshot.flowEdges.length &&
    groups.length === snapshot.groups.length
  ) {
    return snapshot;
  }

  return {
    ...snapshot,
    materialNodes,
    operationNodes,
    flowEdges,
    groups,
  };
}
